import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectorRef, AfterViewInit, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { Messages } from '../../../../common/enums/messages';
import { DocumentService } from '../../../../data/document.service';
import { SignaturePadComponent } from '../../../../shared/components/signature-pad/signature-pad.component';
import { SnackBarService } from '../../../../shared/services/snack-bar.service';
import { GenerateForeignWorkerDeclarationModalData } from './generate-foreign-worker-declaration-modal-data';
import { GenerateForeignWorkerDeclarationRequest } from '../../../../contracts/requests/generate-foreign-worker-declaration-request';

const COMPONENT_ID = 'generate-foreign-worker-declaration';

type ForeignWorkerDeclarationForm = FormGroup<{
  completingDeclarationDate: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  pesel: FormControl<string>;
  dateOfBirth: FormControl<Date>;
}>;

@Component({
  selector: COMPONENT_ID,
  templateUrl: './generate-foreign-worker-declaration.component.html',
  styleUrls: ['./generate-foreign-worker-declaration.component.scss'],
  standalone: false
})
export class GenerateForeignWorkerDeclarationComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(SignaturePadComponent) signaturePad!: SignaturePadComponent;
  public readonly componentId = COMPONENT_ID;
  private readonly unsubscribe$ = new Subject();
  formGroup!: ForeignWorkerDeclarationForm;

  public data = inject<GenerateForeignWorkerDeclarationModalData>(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<GenerateForeignWorkerDeclarationComponent>);
  private formBuilder = inject(FormBuilder);
  private spinner = inject(NgxSpinnerService);
  private snackbar = inject(SnackBarService);
  private documentService = inject(DocumentService);
  private cdr = inject(ChangeDetectorRef);

  public async ngOnInit(): Promise<void> {
    this.buildForm();
  }

  public ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next(undefined);
    this.unsubscribe$.unsubscribe();
  }

  public onClose = () => this.dialogRef.close();

  get dateOfBirthControl() {
    return this.formGroup.controls.dateOfBirth;
  }

  get peselControl() {
    return this.formGroup.controls.pesel;
  }

  get completingDeclarationDate() {
    return new Date();
  }

  get isSignaturePadEmpty(): boolean {
    if (!this.signaturePad) return true;
    return this.signaturePad.isEmpty;
  }

  get isSignatureValid(): boolean {
    if (!this.signaturePad) return false;
    return this.signaturePad.isValid;
  }

  private get request(): GenerateForeignWorkerDeclarationRequest {
    return {
      WorkerId: this.data.WorkerId,
      WorkerFormId: this.data.WorkerFormId,
      SignatureFileBase64: this.signaturePad.signaturePng,
      FutureInPolandAnswerCode: this.data.FutureInPolandAnswerCode,
      ResidentRP183DaysOfTaxYearAnswerCode: this.data.ResidentRP183DaysOfTaxYearAnswerCode
    }
  };

  submit() {
    if (!this.isSignatureValid) {
      this.snackbar.openErrorSnackBar('Please provide a valid signature');
      return;
    }
    
    this.spinner.show();

    this.documentService
      .generateForeignWorkerDeclarationFile(this.request)
      .pipe(
        finalize(() => this.spinner.hide()),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (result: any) => {
          this.snackbar.openSuccessSnackBar(Messages.SuccessfullyGeneratedDocument);
          this.dialogRef.close(result);
        },
        error: (error: any) => {
          this.snackbar.openErrorSnackBar(Messages.SomethingWentWrong);
        }
      });
  }

  dateFilter(date: Date): boolean {
    return date < new Date();
  }

  private buildForm() {
    this.formGroup = this.formBuilder.nonNullable.group({
      completingDeclarationDate: this.formBuilder.nonNullable.control({ value: new Date().toLocaleDateString(), disabled: true }),
      firstName: this.formBuilder.nonNullable.control({ value: this.data.FirstName, disabled: true }),
      lastName: this.formBuilder.nonNullable.control({ value: this.data.LastName, disabled: true }),
      pesel: this.formBuilder.nonNullable.control({ value: this.data.Pesel, disabled: true }),
      dateOfBirth: this.formBuilder.nonNullable.control({ value: this.data.DateOfBirth, disabled: true }),
    });
  }
}
